import { useL } from "@/lib/useL";
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Square, Volume2 } from "lucide-react";

/**
 * Text-to-speech button using the browser's native Web Speech API.
 *
 * Zero cost, zero latency, works offline. Voice quality depends on the OS
 * (very good on iOS / macOS / modern Chrome, decent elsewhere).
 *
 * Props:
 *   text     — plain text to speak (HTML/markdown is stripped internally)
 *   lang     — BCP-47 language tag ("fr", "en", "es"…) — auto-detected fallback
 *   compact  — if true, renders an icon-only button (used inside chapter accordions)
 *   testId   — optional data-testid override
 */
export const SpeakButton = ({ text, lang, compact = false, testId }) => {
  const L = useL();
  const [state, setState] = useState("idle"); // idle | playing | paused
  const utterRef = useRef(null);
  const supported =
    typeof window !== "undefined" &&
    typeof window.speechSynthesis !== "undefined" &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

  // Stop any ongoing speech when the component unmounts or the text changes,
  // so navigating between summaries never leaves audio playing in the background.
  useEffect(() => {
    return () => {
      if (supported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [supported, text]);

  if (!supported) return null;

  const cleanText = (html) => {
    if (!html) return "";
    // Strip HTML tags, then decode a few common entities.
    const stripped = String(html).replace(/<[^>]*>/g, " ");
    const el = document.createElement("textarea");
    el.innerHTML = stripped;
    return el.value.replace(/\s+/g, " ").trim();
  };

  const guessLang = (t) => {
    if (lang && lang !== "auto") return lang;
    // Very light heuristic: French diacritics → fr, otherwise fall back to
    // navigator language or English. Web Speech API is forgiving.
    if (/[àâäéèêëîïôöùûüÿçœ]/i.test(t)) return "fr";
    return (typeof navigator !== "undefined" && navigator.language) || "en";
  };

  const speak = () => {
    const plain = cleanText(text);
    if (!plain) return;
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(plain);
    utter.lang = guessLang(plain);
    utter.rate = 1.0;
    utter.pitch = 1.0;
    utter.onstart = () => setState("playing");
    utter.onpause = () => setState("paused");
    utter.onresume = () => setState("playing");
    utter.onend = () => setState("idle");
    utter.onerror = () => setState("idle");
    utterRef.current = utter;
    window.speechSynthesis.speak(utter);
  };

  const pause = () => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setState("paused");
    }
  };

  const resume = () => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setState("playing");
    }
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setState("idle");
  };

  const onPrimary = () => {
    if (state === "idle") speak();
    else if (state === "playing") pause();
    else if (state === "paused") resume();
  };

  const label =
    state === "idle" ? L("Écouter", "Listen") : state === "playing" ? L("Pause", "Pause") : L("Reprendre", "Resume");
  const Icon = state === "playing" ? Pause : Play;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={onPrimary}
          data-testid={testId || "speak-btn"}
          aria-label={label}
          title={label}
          className="w-9 h-9 flex items-center justify-center border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors"
        >
          <Icon className="w-4 h-4" />
        </button>
        {state !== "idle" && (
          <button
            type="button"
            onClick={stop}
            data-testid={(testId || "speak-btn") + "-stop"}
            aria-label={L("Arrêter la lecture", "Stop reading")}
            title={L("Arrêter", "Stop")}
            className="w-9 h-9 flex items-center justify-center border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] transition-colors"
          >
            <Square className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={onPrimary}
        data-testid={testId || "speak-btn"}
        className="font-mono uppercase tracking-[0.2em] text-[11px] px-3 py-2 border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2"
      >
        {state === "idle" ? (
          <Volume2 className="w-3.5 h-3.5" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
        {label}
      </button>
      {state !== "idle" && (
        <button
          type="button"
          onClick={stop}
          data-testid={(testId || "speak-btn") + "-stop"}
          className="font-mono uppercase tracking-[0.2em] text-[11px] px-3 py-2 border border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
        >
          <Square className="w-3 h-3" />
          Stop
        </button>
      )}
    </div>
  );
};

export default SpeakButton;
